/**
 * PATH: packages/email-hub/src/feature/pages/inboxSelection.ts
 * PURPOSE: Pure selection helpers for the inbox list pane, extracted so
 *          they can be unit-tested without rendering the (very large)
 *          InboxPage component.
 */

/**
 * Pick the row to select after the currently-open row is removed from the
 * visible list (binned / archived / deleted).
 *
 * Operator (2026-06-01): "I deleted an email and instead of going to the
 * next email it just shows 'select an email to preview'." Gmail/Outlook
 * keep the reader flowing by advancing to the NEXT row; if the removed row
 * was the last one, they fall back to the PREVIOUS row; only when the list
 * empties do they clear the preview.
 *
 * Pure I→O: takes the ordered list of visible ids + the id being removed,
 * returns the id to select next (or ``null`` when nothing remains / the id
 * isn't in the list).
 */
/**
 * Checkbox click updates one id and moves the anchor.
 * Shift-click replaces the selection with the inclusive range from the
 * anchor through the clicked row, in the current visible order. The
 * anchor stays put so a second shift-click retargets the same range.
 */
export function applyEmailSelection(args: {
  orderedIds: readonly string[]
  selected: ReadonlySet<string>
  anchorId: string | null
  clickedId: string
  shift: boolean
}): { selected: Set<string>; anchorId: string | null } {
  const { orderedIds, selected, anchorId, clickedId, shift } = args
  const anchorIndex = anchorId == null ? -1 : orderedIds.indexOf(anchorId)
  const clickIndex = orderedIds.indexOf(clickedId)
  if (shift && anchorIndex >= 0 && clickIndex >= 0) {
    const start = Math.min(anchorIndex, clickIndex)
    const end = Math.max(anchorIndex, clickIndex)
    return {
      selected: new Set(orderedIds.slice(start, end + 1)),
      anchorId,
    }
  }
  const next = new Set(selected)
  if (next.has(clickedId)) next.delete(clickedId)
  else next.add(clickedId)
  return { selected: next, anchorId: clickedId }
}

/**
 * Right-click inside a multi-selection acts on every selected row, in
 * list order. Right-click outside that selection acts on the clicked
 * row alone and the selection becomes that row.
 */
export function contextActionIds(args: {
  orderedIds: readonly string[]
  selected: ReadonlySet<string>
  clickedId: string
}): { ids: string[]; selection: Set<string>; replaced: boolean } {
  const { orderedIds, selected, clickedId } = args
  if (selected.has(clickedId) && selected.size > 1) {
    const ids = orderedIds.filter((id) => selected.has(id))
    return {
      ids: ids.length > 0 ? ids : [clickedId],
      selection: new Set(selected),
      replaced: false,
    }
  }
  const alreadyOnlyThis = selected.size === 1 && selected.has(clickedId)
  return {
    ids: [clickedId],
    selection: new Set([clickedId]),
    replaced: !alreadyOnlyThis,
  }
}

export function nextSelectionAfterRemoval(
  ids: readonly string[],
  removedId: string,
): string | null {
  const idx = ids.indexOf(removedId)
  if (idx === -1) return null
  return ids[idx + 1] ?? ids[idx - 1] ?? null
}
